import { Image as ImageIcon, Upload, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, Pill } from '../_primitives';

export interface HeroImageCardProps {
  /** When set, renders the image preview instead of the empty state. */
  imageUrl?: string | null;
  /** Optional alt text shown beneath the image. */
  imageAlt?: string | null;
  /** WordPress featured image ID from DB (indicates image exists even if URL not loaded). */
  featuredImageId?: number | null;
  /** Recommended dimensions hint, e.g. "1200×630". */
  recommendedSize?: string;
  onUpload?: () => void;
  onGenerate?: () => void;
  /** Disables both action buttons (e.g. while uploading). */
  busy?: boolean;
}

export function HeroImageCard({
  imageUrl,
  imageAlt,
  featuredImageId,
  recommendedSize = '1200×630',
  onUpload,
  onGenerate,
  busy = false,
}: HeroImageCardProps) {
  // Image is "Set" if we have a local URL (from generation) OR a featured_image_id from DB
  const hasLocalImage = Boolean(imageUrl);
  const hasDbImage = Boolean(featuredImageId);
  const hasImage = hasLocalImage || hasDbImage;

  return (
    <Card railSection="hero-image">
      <CardHeader
        icon={<ImageIcon className="size-3.5 text-coral" />}
        title="Hero image"
        trailing={
          <Pill tone={hasImage ? 'success' : 'warning'} size="sm">
            {hasImage ? 'Set' : 'Missing'}
          </Pill>
        }
      />

      <div className="px-3 pb-3">
        {hasImage ? (
          <div className="aspect-video rounded-md mb-2 overflow-hidden border border-border bg-warm-bg">
            {hasLocalImage ? (
              <img
                src={imageUrl ?? ''}
                alt={imageAlt ?? 'Hero image'}
                className="w-full h-full object-cover"
              />
            ) : (
              // DB has featured_image_id but no local URL - show WordPress placeholder
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-warm-bg">
                <ImageIcon className="size-8 text-coral/60" />
                <div className="text-[11px] text-muted-foreground text-center px-4">
                  Featured image set in WordPress
                  {featuredImageId && <span className="block mt-0.5">(Media ID: {featuredImageId})</span>}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            className={cn(
              'aspect-video rounded-md mb-2 px-3.5 py-3.5 text-center',
              'flex flex-col items-center justify-center gap-1',
              'border-[1.5px] border-dashed border-border',
              'bg-warm-bg',
              'bg-[repeating-linear-gradient(135deg,_var(--color-card)_0px,_var(--color-card)_10px,_var(--color-warm-bg)_10px,_var(--color-warm-bg)_20px)]',
            )}
          >
            <ImageIcon className="size-5 text-muted-foreground" />
            <div className="text-[12px] font-semibold text-foreground">Add a hero image</div>
            <div className="text-[10.5px] text-muted-foreground leading-snug">
              {recommendedSize} recommended
            </div>
          </div>
        )}

        <div className="flex gap-1.5">
          {onUpload && (
            <button
              type="button"
              onClick={onUpload}
              disabled={busy}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11.5px] font-medium border cursor-pointer',
                'bg-card text-foreground border-border hover:bg-warm-bg',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <Upload className="size-3" />
              Upload
            </button>
          )}
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy || !onGenerate}
            className={cn(
              'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11.5px] font-semibold border-0 text-white cursor-pointer',
              'bg-gradient-to-br from-coral to-coral-hover hover:opacity-90',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <Sparkles className="size-3" />
            Generate AI
          </button>
        </div>
      </div>
    </Card>
  );
}
